import React, { useState, useRef } from 'react';
import type { Settings, PortfolioLayout, ProjectDetailLayout } from '../../types';
import { storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useToast } from '../../hooks/useToast';
import { Link } from 'react-router-dom';

interface AdminSettingsPageProps {
    settings: Settings;
    onSave: (settings: Settings) => void;
}

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;

// Icons for Portfolio Layout Options
const LayoutGridIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} viewBox="0 0 32 32"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="12" y="2" width="8" height="8" rx="1"/><rect x="22" y="2" width="8" height="8" rx="1"/><rect x="2" y="12" width="8" height="8" rx="1"/><rect x="12" y="12" width="8" height="8" rx="1"/><rect x="22" y="12" width="8" height="8" rx="1"/></svg>;
const LayoutMasonryIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} viewBox="0 0 32 32"><rect x="2" y="2" width="8" height="12" rx="1"/><rect x="12" y="2" width="8" height="28" rx="1"/><rect x="22" y="2" width="8" height="18" rx="1"/><rect x="2" y="16" width="8" height="14" rx="1"/></svg>;
const LayoutFullscreenIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} viewBox="0 0 32 32"><rect x="2" y="4" width="28" height="24" rx="1"/><path d="M2 12h28M2 20h28"/></svg>;

// Icons for Detail Page Layout Options
const LayoutClassicIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} viewBox="0 0 40 32"><rect x="2" y="2" width="36" height="10" rx="1"/><rect x="2" y="16" width="22" height="14" rx="1"/><rect x="28" y="16" width="10" height="14" rx="1"/></svg>;
const LayoutSplitIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} viewBox="0 0 40 32"><rect x="2" y="2" width="16" height="28" rx="1"/><rect x="22" y="2" width="16" height="12" rx="1"/><rect x="22" y="18" width="16" height="12" rx="1"/></svg>;
const LayoutImmersiveIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} viewBox="0 0 40 32"><rect x="2" y="2" width="36" height="10" rx="1"/><rect x="2" y="16" width="36" height="4" rx="1"/><rect x="2" y="24" width="36" height="6" rx="1"/></svg>;

const ToggleSwitch: React.FC<{ enabled: boolean; onChange: () => void; }> = ({ enabled, onChange }) => (
    <button
        type="button"
        className={`${enabled ? 'bg-gray-800' : 'bg-gray-300'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
        onClick={onChange}
        aria-checked={enabled}
        role="switch"
    >
        <span className={`${enabled ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform`} />
    </button>
);


interface ImageUploaderProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ label, value, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { showToast } = useToast();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `settings/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                onChange(downloadURL);
            } catch (error: any) {
                console.error("Upload failed:", error);
                let message = "Upload failed. Please try again.";
                if (error.code === 'storage/unauthorized') {
                    message = 'Permission Denied: Please check your Firebase Storage rules to allow write access.';
                }
                showToast(message, { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div 
                className="relative w-40 h-40 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors bg-gray-50"
                onClick={() => !isUploading && inputRef.current?.click()}
            >
                {isUploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                        <div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div>
                    </div>
                )}
                {value ? (
                    <img src={value} alt={label} className="h-full w-full object-contain p-2 rounded-md" />
                ) : (
                    <div className="text-center text-gray-500">
                        <UploadIcon className="mx-auto h-8 w-8"/>
                        <span className="mt-2 text-sm">Click to upload</span>
                    </div>
                )}
            </div>
            <input 
                type="file"
                ref={inputRef}
                className="hidden"
                accept="image/png, image/jpeg, image/gif, image/svg+xml, image/webp"
                onChange={handleFileChange}
                disabled={isUploading}
            />
        </div>
    );
};

const AdminSettingsPage: React.FC<AdminSettingsPageProps> = ({ settings, onSave }) => {
    const [formState, setFormState] = useState<Settings>(settings);
    const [newCategory, setNewCategory] = useState('');
    const [activeThemeTab, setActiveThemeTab] = useState<'frontend' | 'backend'>('frontend');
    const [isSaving, setIsSaving] = useState(false);
    const { showToast } = useToast();
    const [isTestingEmail, setIsTestingEmail] = useState(false);

    const handleTestEmail = async () => {
        if (!formState.email) {
            showToast('Please configure a Contact Email address first.', { type: 'error' });
            return;
        }

        setIsTestingEmail(true);
        try {
            const response = await fetch('/api/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    test: true,
                    toEmail: formState.email,
                    senderEmail: formState.resendSenderEmail
                })
            });

            const text = await response.text();
            let data: any = {};
            let isJson = true;
            try {
                data = JSON.parse(text);
            } catch (e) {
                isJson = false;
            }

            if (response.ok && isJson) {
                showToast('Test email sent successfully! Please check your inbox.', { type: 'success' });
            } else {
                let errorMessage = 'Unknown error';
                if (!isJson) {
                    if (window.location.hostname === 'localhost') {
                        errorMessage = 'Serverless functions are not running locally. Please run your app using "npx vercel dev" instead of "npm run dev" to test serverless API routes locally.';
                    } else {
                        errorMessage = 'The server did not return a valid JSON response. Please check server logs.';
                    }
                } else {
                    errorMessage = data.error || errorMessage;
                }
                showToast(`Failed to send test email: ${errorMessage}`, { type: 'error' });
            }
        } catch (error: any) {
            showToast(`Connection error: ${error.message || 'Could not connect to test server'}`, { type: 'error' });
        } finally {
            setIsTestingEmail(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleGoogleMapsUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let finalValue = value;
    
        // Check if the user pasted an iframe tag and extract the src
        if (value.trim().startsWith('<iframe')) {
            const match = value.match(/src="([^"]*)"/);
            if (match && match[1]) {
                finalValue = match[1];
                showToast('Iframe code detected. Extracted the URL for you!', { type: 'info' });
            }
        }
        
        setFormState(prev => ({ ...prev, [name]: finalValue }));
    };
    
    const handlePortfolioLayoutChange = (layout: PortfolioLayout) => {
        setFormState(prev => ({ ...prev, portfolioLayout: layout }));
    };

    const handleDetailLayoutChange = (layout: ProjectDetailLayout) => {
        setFormState(prev => ({ ...prev, detailLayout: layout }));
    };

    const handleNestedChange = (section: 'seo' | 'openGraph' | 'frontendTheme' | 'backendTheme') => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (section === 'frontendTheme') {
             setFormState(prev => ({
                ...prev,
                theme: { ...prev.theme, frontendTheme: { ...prev.theme.frontendTheme, [name]: value } },
            }));
        } else if (section === 'backendTheme') {
              setFormState(prev => ({
                ...prev,
                theme: { ...prev.theme, backendTheme: { ...prev.theme.backendTheme, [name]: value } },
            }));
        }else {
             setFormState(prev => ({
                ...prev,
                [section]: { ...prev[section], [name]: value }
            }));
        }
    };
    
     const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            theme: {
                ...prev.theme,
                frontendTheme: {
                    ...prev.theme.frontendTheme,
                    fontFamily: {
                        ...prev.theme.frontendTheme.fontFamily,
                        [name]: value,
                    }
                }
            }
        }));
    };
    
    const handleAddCategory = () => {
        if (newCategory.trim()) {
            setFormState(prev => ({ ...prev, projectCategories: [...(prev.projectCategories || []), newCategory.trim()] }));
            setNewCategory('');
        }
    };
    
    const handleRemoveCategory = (index: number) => {
        setFormState(prev => ({ ...prev, projectCategories: prev.projectCategories.filter((_, i) => i !== index) }));
    };

    const handleSocialLinkChange = (index: number, field: 'platform' | 'url', value: string) => {
        const newSocialLinks = [...formState.socialLinks];
        newSocialLinks[index] = { ...newSocialLinks[index], [field]: value };
        setFormState(prev => ({ ...prev, socialLinks: newSocialLinks }));
    };

    const handleAddSocialLink = () => {
        setFormState(prev => ({ ...prev, socialLinks: [...prev.socialLinks, { platform: '', url: ''}] }));
    };
    
    const handleRemoveSocialLink = (index: number) => {
        setFormState(prev => ({ ...prev, socialLinks: prev.socialLinks.filter((_, i) => i !== index) }));
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onSave(formState);
            showToast('Settings saved successfully!', { type: 'success' });
        } catch (error) {
            showToast('Failed to save settings.', { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };
    
    const fontOptions = ['Inter', 'Playfair Display', 'Montserrat', 'Roboto Mono', 'Source Sans Pro', 'Lato', 'Raleway', 'Poppins', 'Nunito Sans', 'Open Sans'];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                 <h2 className="text-3xl font-bold text-gray-800">Global Settings</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Company Profile Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Company Profile</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name</label>
                            <input type="text" name="companyName" id="companyName" value={formState.companyName} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="slogan" className="block text-sm font-medium text-gray-700">Company Slogan</label>
                            <input type="text" name="slogan" id="slogan" value={formState.slogan} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div className="flex items-start gap-4">
                             <ImageUploader label="Logo" value={formState.logoUrl} onChange={(val) => setFormState(p => ({...p, logoUrl: val}))}/>
                             <div>
                                <label htmlFor="logoWidth" className="block text-sm font-medium text-gray-700">Logo Width (px)</label>
                                <input 
                                    type="number" 
                                    name="logoWidth" 
                                    id="logoWidth" 
                                    value={formState.logoWidth || 120} 
                                    onChange={(e) => setFormState(p => ({...p, logoWidth: Number(e.target.value)}))} 
                                    className="mt-1 block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                                />
                                <p className="mt-1 text-xs text-gray-500">Set the display width for the logo in the public site header.</p>
                             </div>
                        </div>
                        <div/>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Contact Email</label>
                            <input type="email" name="email" id="email" value={formState.email} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <input type="tel" name="phone" id="phone" value={formState.phone} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                         <div className="col-span-1 md:col-span-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
                            <input type="text" name="address" id="address" value={formState.address} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div className="col-span-1 md:col-span-2">
                            <label htmlFor="googleMapsUrl" className="block text-sm font-medium text-gray-700">Google Maps Embed URL</label>
                            <input type="text" name="googleMapsUrl" id="googleMapsUrl" value={formState.googleMapsUrl || ''} onChange={handleGoogleMapsUrlChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                            <p className="mt-1 text-xs text-gray-500">From Google Maps: click "Share" &rarr; "Embed a map". You can paste the entire iframe code or just the URL from the `src="..."` part.</p>
                        </div>
                        <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="emailService" className="block text-sm font-medium text-gray-700">Email Delivery Service</label>
                                <select name="emailService" id="emailService" value={formState.emailService || 'formspree'} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 bg-white">
                                    <option value="formspree">Formspree</option>
                                    <option value="resend">Resend API (Serverless)</option>
                                </select>
                            </div>
                            {formState.emailService === 'resend' ? (
                                <div>
                                    <label htmlFor="resendSenderEmail" className="block text-sm font-medium text-gray-700">Resend Sender Email</label>
                                    <input type="text" name="resendSenderEmail" id="resendSenderEmail" value={formState.resendSenderEmail || ''} onChange={handleChange} placeholder="ArkitekNS Contact <onboarding@resend.dev>" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                                </div>
                            ) : (
                                <div>
                                    <label htmlFor="formspreeEndpoint" className="block text-sm font-medium text-gray-700">Contact Form Endpoint</label>
                                    <input type="url" name="formspreeEndpoint" id="formspreeEndpoint" value={formState.formspreeEndpoint || ''} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                                </div>
                            )}
                        </div>

                        {formState.emailService === 'resend' && (
                            <div className="col-span-1 md:col-span-2 bg-gray-50 p-4 border border-gray-200 rounded-md flex justify-between items-center">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-800">Resend API Integration Setup</h4>
                                    <p className="text-xs text-gray-500 mt-1">Make sure you set the <code>RESEND_API_KEY</code> environment variable in your local .env or Vercel environment settings.</p>
                                </div>
                                <button
                                    type="button"
                                    disabled={isTestingEmail}
                                    onClick={handleTestEmail}
                                    className="px-4 py-2 bg-gray-800 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:bg-gray-400 flex items-center gap-2"
                                >
                                    {isTestingEmail ? (
                                        <>
                                            <span className="inline-block w-4 h-4 border-2 border-dashed border-white rounded-full animate-spin"></span>
                                            Testing...
                                        </>
                                    ) : (
                                        'Test Email Setup'
                                    )}
                                </button>
                            </div>
                        )}
                         <div className="col-span-1 md:col-span-2">
                            <label htmlFor="footerContent" className="block text-sm font-medium text-gray-700">Footer Content</label>
                            <textarea name="footerContent" id="footerContent" value={formState.footerContent} onChange={handleChange} rows={2} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"></textarea>
                        </div>
                    </div>
                </div>
                
                {/* Portfolio Display Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Portfolio & Project Display</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Portfolio Page Layout</label>
                        <p className="text-sm text-gray-500 mb-4">Choose how projects are displayed on the main portfolio page.</p>
                        <div className="grid grid-cols-3 gap-4">
                            {(['grid', 'masonry', 'fullscreen'] as PortfolioLayout[]).map(layout => (
                                <button
                                    key={layout}
                                    type="button"
                                    onClick={() => handlePortfolioLayoutChange(layout)}
                                    className={`p-4 border rounded-md text-center transition-all ${formState.portfolioLayout === layout ? 'bg-gray-800 text-white border-gray-800 shadow-lg' : 'bg-white hover:border-gray-500 hover:shadow-md'}`}
                                >
                                    {layout === 'grid' && <LayoutGridIcon className={`mx-auto h-12 w-12 ${formState.portfolioLayout === layout ? 'fill-white' : 'fill-gray-400'}`} />}
                                    {layout === 'masonry' && <LayoutMasonryIcon className={`mx-auto h-12 w-12 ${formState.portfolioLayout === layout ? 'fill-white' : 'fill-gray-400'}`} />}
                                    {layout === 'fullscreen' && <LayoutFullscreenIcon className={`mx-auto h-12 w-12 ${formState.portfolioLayout === layout ? 'fill-white' : 'fill-gray-400'}`} />}
                                    <span className="block text-xs font-semibold uppercase mt-2 tracking-wider capitalize">{layout}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                     <div className="mt-8">
                        <label className="block text-sm font-medium text-gray-700">Project Detail Page Layout</label>
                        <p className="text-sm text-gray-500 mb-4">Choose a consistent layout for all project detail pages.</p>
                        <div className="grid grid-cols-3 gap-4">
                            {(['classic', 'split', 'immersive'] as ProjectDetailLayout[]).map(layout => (
                                <button
                                    key={layout}
                                    type="button"
                                    onClick={() => handleDetailLayoutChange(layout)}
                                    className={`p-4 border rounded-md text-center transition-all ${formState.detailLayout === layout ? 'bg-gray-800 text-white border-gray-800 shadow-lg' : 'bg-white hover:border-gray-500 hover:shadow-md'}`}
                                >
                                    {layout === 'classic' && <LayoutClassicIcon className={`mx-auto h-12 w-12 ${formState.detailLayout === layout ? 'fill-white' : 'fill-gray-400'}`} />}
                                    {layout === 'split' && <LayoutSplitIcon className={`mx-auto h-12 w-12 ${formState.detailLayout === layout ? 'fill-white' : 'fill-gray-400'}`} />}
                                    {layout === 'immersive' && <LayoutImmersiveIcon className={`mx-auto h-12 w-12 ${formState.detailLayout === layout ? 'fill-white' : 'fill-gray-400'}`} />}
                                    <span className="block text-xs font-semibold uppercase mt-2 tracking-wider capitalize">{layout}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-8 border-t pt-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Enable "Year" Filter</label>
                                <p className="mt-1 text-xs text-gray-500">Show the "Year" dropdown on the portfolio page.</p>
                            </div>
                            <ToggleSwitch 
                                enabled={formState.enableYearFilter} 
                                onChange={() => setFormState(prev => ({...prev, enableYearFilter: !prev.enableYearFilter}))} 
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Enable "Status" Filter</label>
                                <p className="mt-1 text-xs text-gray-500">Show the "Status" dropdown on the portfolio page.</p>
                            </div>
                             <ToggleSwitch 
                                enabled={formState.enableStatusFilter} 
                                onChange={() => setFormState(prev => ({...prev, enableStatusFilter: !prev.enableStatusFilter}))} 
                            />
                        </div>
                    </div>
                </div>

                {/* UI & Theme Section */}
                 <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">UI & Theme</h3>
                     <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            <button type="button" onClick={() => setActiveThemeTab('frontend')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeThemeTab === 'frontend' ? 'border-gray-800 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                                Frontend Theme
                            </button>
                            <button type="button" onClick={() => setActiveThemeTab('backend')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeThemeTab === 'backend' ? 'border-gray-800 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                                Backend Theme
                            </button>
                        </nav>
                    </div>
                    {activeThemeTab === 'frontend' && (
                        <div className="pt-6">
                             <h4 className="text-lg font-semibold text-gray-800 mb-4">Colors</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[
                                    { name: 'primaryColor', label: 'Primary Text/Accent' },
                                    { name: 'pageBackgroundColor', label: 'Main Page Background' },
                                    { name: 'secondaryColor', label: 'Section Background' },
                                    { name: 'headerBackgroundColor', label: 'Header Background' },
                                    { name: 'footerBackgroundColor', label: 'Footer Background' },
                                    { name: 'navLinkColor', label: 'Nav Link (Default)' },
                                    { name: 'activeLinkColor', label: 'Nav Link (Active)' },
                                    { name: 'navLinkHoverColor', label: 'Nav Link (Hover)' },
                                ].map(item => (
                                    <div key={item.name}>
                                        <label htmlFor={`fe-${item.name}`} className="block text-sm font-medium text-gray-700">{item.label}</label>
                                        <div className="relative mt-1">
                                            <input type="text" name={item.name} id={`fe-${item.name}`} value={formState.theme.frontendTheme[item.name as keyof Omit<typeof formState.theme.frontendTheme, 'fontFamily'>]} onChange={handleNestedChange('frontendTheme')} className="block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 text-sm"/>
                                            <div className="absolute inset-y-0 left-0 flex items-center">
                                                <input type="color" value={formState.theme.frontendTheme[item.name as keyof Omit<typeof formState.theme.frontendTheme, 'fontFamily'>]} onChange={handleNestedChange('frontendTheme')} name={item.name} className="h-full w-10 rounded-l-md border-0 cursor-pointer"/>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                             <div className="mt-8 border-t pt-6">
                                 <h4 className="text-lg font-semibold text-gray-800 mb-4">Typography</h4>
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <label htmlFor="font-header" className="block text-sm font-medium text-gray-700">Header/Menu Font</label>
                                        <select name="header" id="font-header" value={formState.theme.frontendTheme.fontFamily.header} onChange={handleFontChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                            {fontOptions.map(font => <option key={font} value={font}>{font}</option>)}
                                        </select>
                                    </div>
                                     <div>
                                        <label htmlFor="font-heading" className="block text-sm font-medium text-gray-700">Headings Font</label>
                                        <select name="heading" id="font-heading" value={formState.theme.frontendTheme.fontFamily.heading} onChange={handleFontChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                            {fontOptions.map(font => <option key={font} value={font}>{font}</option>)}
                                        </select>
                                    </div>
                                     <div>
                                        <label htmlFor="font-body" className="block text-sm font-medium text-gray-700">Body/Content Font</label>
                                        <select name="body" id="font-body" value={formState.theme.frontendTheme.fontFamily.body} onChange={handleFontChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                           {fontOptions.map(font => <option key={font} value={font}>{font}</option>)}
                                        </select>
                                    </div>
                                 </div>
                            </div>
                        </div>
                    )}
                     {activeThemeTab === 'backend' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                             <div>
                                <label htmlFor="be-adminSidebarColor" className="block text-sm font-medium text-gray-700">Admin Sidebar</label>
                                <div className="relative mt-1">
                                    <input type="text" name="adminSidebarColor" id="be-adminSidebarColor" value={formState.theme.backendTheme.adminSidebarColor} onChange={handleNestedChange('backendTheme')} className="mt-1 block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 text-sm"/>
                                    <div className="absolute inset-y-0 left-0 flex items-center">
                                        <input type="color" value={formState.theme.backendTheme.adminSidebarColor} onChange={handleNestedChange('backendTheme')} name="adminSidebarColor" className="h-full w-10 rounded-l-md border-0 cursor-pointer"/>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="be-adminIconColor" className="block text-sm font-medium text-gray-700">Admin Icon Color</label>
                                <div className="relative mt-1">
                                    <input type="text" name="adminIconColor" id="be-adminIconColor" value={formState.theme.backendTheme.adminIconColor} onChange={handleNestedChange('backendTheme')} className="mt-1 block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 text-sm"/>
                                    <div className="absolute inset-y-0 left-0 flex items-center">
                                        <input type="color" value={formState.theme.backendTheme.adminIconColor} onChange={handleNestedChange('backendTheme')} name="adminIconColor" className="h-full w-10 rounded-l-md border-0 cursor-pointer"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Services & Categories */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Project Categories</h3>
                    <p className="text-sm text-gray-500 mb-4">
                        Manage your company's services in the dedicated{" "}
                        <Link to="/admin/services" className="text-indigo-600 hover:underline">Our Services</Link> page.
                    </p>
                    <div className="space-y-3 max-h-48 overflow-y-auto pr-4">
                        {(formState.projectCategories || []).map((category, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded mr-2">
                                <p className="text-gray-800 text-sm break-all pr-2">{category}</p>
                                <button 
                                    type="button" 
                                    onClick={() => handleRemoveCategory(index)} 
                                    className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors text-sm font-bold"
                                    aria-label={`Remove ${category}`}
                                >
                                    X
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                        <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Add a new category" className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500" />
                        <button type="button" onClick={handleAddCategory} className="w-10 h-10 flex-shrink-0 bg-gray-800 text-white rounded-md hover:bg-gray-700 whitespace-nowrap flex items-center justify-center font-bold text-xl">+</button>
                    </div>
                </div>

                 {/* Social Media Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Social Media Links</h3>
                    <div className="space-y-4">
                        {formState.socialLinks.map((link, index) => (
                            <div key={index} className="flex items-center gap-4">
                               <input type="text" value={link.platform} onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)} placeholder="Platform (e.g., Facebook)" className="w-1/3 px-3 py-2 border border-gray-300 rounded-md"/>
                               <input type="url" value={link.url} onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)} placeholder="Full URL (e.g., https://...)" className="flex-1 px-3 py-2 border border-gray-300 rounded-md"/>
                               <button 
                                    type="button" 
                                    onClick={() => handleRemoveSocialLink(index)} 
                                    className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors text-sm font-bold"
                                    aria-label="Remove social link"
                                >
                                    X
                               </button>
                            </div>
                        ))}
                    </div>
                    <button type="button" onClick={handleAddSocialLink} className="mt-4 text-sm font-semibold text-gray-800 hover:text-black inline-flex items-center gap-2">
                        <span className="font-bold text-lg leading-none">+</span> Add Social Link
                    </button>
                </div>

                {/* SEO Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">SEO & Metadata</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                        <div className="space-y-6 lg:col-span-2">
                             <div>
                                <label htmlFor="seo-title" className="block text-sm font-medium text-gray-700">Browser Title</label>
                                <input type="text" name="title" id="seo-title" value={formState.seo.title} onChange={handleNestedChange('seo')} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                            </div>
                            <div>
                                <label htmlFor="seo-description" className="block text-sm font-medium text-gray-700">Site Meta Description</label>
                                <textarea name="description" id="seo-description" value={formState.seo.description} onChange={(e) => handleNestedChange('seo')({ target: { name: 'description', value: e.target.value } } as any)} rows={4} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"></textarea>
                            </div>
                        </div>
                         <ImageUploader label="Favicon" value={formState.seo.favicon} onChange={(val) => setFormState(p => ({...p, seo: {...p.seo, favicon: val}}))}/>
                    </div>
                </div>

                {/* Open Graph Section */}
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Open Graph (Social Sharing)</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                        <div className="space-y-6 lg:col-span-2">
                             <div>
                                <label htmlFor="og-title" className="block text-sm font-medium text-gray-700">OG Title</label>
                                <input type="text" name="title" id="og-title" value={formState.openGraph.title} onChange={handleNestedChange('openGraph')} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                            </div>
                            <div>
                                <label htmlFor="og-description" className="block text-sm font-medium text-gray-700">OG Description</label>
                                <textarea name="description" id="og-description" value={formState.openGraph.description} onChange={(e) => handleNestedChange('openGraph')({ target: { name: 'description', value: e.target.value } } as any)} rows={4} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"></textarea>
                            </div>
                        </div>
                        <ImageUploader label="OG Image" value={formState.openGraph.imageUrl} onChange={(val) => setFormState(p => ({...p, openGraph: {...p.openGraph, imageUrl: val}}))}/>
                    </div>
                </div>
                
                <div className="flex justify-end items-center space-x-4 sticky bottom-0 bg-white/80 backdrop-blur-sm py-4 rounded-b-lg">
                    <button type="submit" disabled={isSaving} className="px-8 py-3 bg-gray-800 text-white rounded-md hover:bg-gray-700 text-base font-semibold disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save All Settings'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default AdminSettingsPage;